import './TreeNode.css';
import React, { FC, ReactNode, useState, Children, useEffect, useContext, useRef, Context } from "react";
import { observer } from 'mobx-react-lite';
import icons from '../../../../icons';
import cx from 'classnames';
import { LeftPaneContext } from "../LeftPaneContext";
import { useCollapsibleState } from '../../../../components/collapsible_state';

const ErrorBadge = () => {
  return (
    <span className='ml_validator_badge_left'>
      <span className='fas fa-exclamation' style={{color: 'rgb(255, 255, 255)', fontSize: '10px'}}></span>
    </span>
  );
};

interface TreeNodeLabelProps {
  name: string | ReactNode
  icon?: string | undefined
  isSelected?: boolean;
  isInvalid?: boolean;
  onClick?: () => void;
}

export const TreeNodeLabel: FC<TreeNodeLabelProps> = observer(({name, icon, isSelected, isInvalid, onClick}) => {

  const handleClick = (e) => {
    e.stopPropagation();
    if(onClick) {
      onClick();
    }
  };

  return (
    <div className={cx("tree-node-label", {"tree-node-label-button": onClick !== undefined})} onClick={handleClick} >
      {isInvalid ? <ErrorBadge /> : icon && <i className={cx(icon, 'tree-node-label-icon')}></i>}
      <span className={cx('tree-node-label-text', 'text-ellipsis', {'tree-node-label-text-selected': isSelected})}>{name}</span>
    </div>
  );
});

export interface SequenceReferenceTreeNodeLabelProps extends TreeNodeLabelProps {
  sequenceName: string;
}

export const SequenceReferenceTreeNodeLabel: FC<SequenceReferenceTreeNodeLabelProps> = observer((props) => {
  const {navigateToReferencedSequence} = useContext(LeftPaneContext);

  return (
    <div className="sequence-reference">
      <TreeNodeLabel {...props} />
      <button
        title="Go to sequence"
        onClick={() => navigateToReferencedSequence(props.sequenceName)}
      >
        <i className="fas fa-external-link-alt"/>
      </button>
    </div>
  );
});


interface TreeNodeProps {
    root?: boolean;
    title: ReactNode | string;
    id?: string;
    keepOpenState?: boolean;
    titleClassName?: string;
    isCollapsible?: boolean;
    isDefaultExpanded?: boolean;
    isSelected?: boolean;
    children?: ReactNode;
}

interface RootTreeNodeProps extends TreeNodeProps {
  isOpen: boolean;
  handleToggle: () => void;
  rootElementRef: React.RefObject<HTMLDivElement>;
}

export const RootTreeNode: FC<RootTreeNodeProps> = observer(({title, titleClassName, isSelected, isOpen, handleToggle, children, rootElementRef}) => (
  <div ref={rootElementRef} className={cx("tree-node tree-node-root", {"tree-selected": isSelected})}>
    <div className={cx("tree-node-icon", {"tree-node-icon-closed": !isOpen})} onClick={handleToggle}>
      <div className="tree-node-icon-pressable-area"></div>
      <i className={icons.caretDown}></i>
    </div>

    <div className="tree-node-content-container">
      <div className={cx('tree-node-title', titleClassName)}>
        {title}
      </div>
      {children && <div className={
        cx("tree-node-children", {
          "tree-node-children-closed": !isOpen
        })
      } >
        {children}
      </div>
      }
    </div>
  </div>
));

// root is always collapsible
// children can be collapsible but they will have different stylings from a root node
export const TreeNode: FC<TreeNodeProps> = observer(({
  root,
  title,
  id,
  keepOpenState = false,
  titleClassName = '',
  isCollapsible,
  isDefaultExpanded = true,
  isSelected,
  children
}) => {

  const rootElementRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useCollapsibleState({
    id: `${id || title}`,
    keepState: keepOpenState,
    defaultIsOpen: isDefaultExpanded,
  });
  const [count] = useState(Children.count(children));

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if(count < Children.count(children)) {
      setIsOpen(true);
    }
  },[Children.count(children)]);

  useEffect(() => {
    if (isSelected) {
      rootElementRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    }
  }, [isSelected]);

  if (root) {
    return (
      <RootTreeNode
        title={title}
        titleClassName={titleClassName}
        isOpen={isOpen}
        handleToggle={handleToggle}
        isSelected={isSelected || false}
        children={children}
        rootElementRef={rootElementRef}
      />
    );
  }

  return (
    <div ref={rootElementRef} className={cx("tree-node", {"tree-selected": isSelected})}>
      <div className="tree-node-content-container">
        <div className={cx('tree-node-title', titleClassName)}>
          {
            isCollapsible &&
            <div className={cx("tree-node-icon", "tree-node-icon-on-branch", {"tree-node-icon-closed": !isOpen})} onClick={handleToggle} >
              <div className="tree-node-icon-pressable-area"></div>
              <i className={icons.caretDown}></i>
            </div>
          }
          {title}
        </div>
        {children && <div className={
          cx("tree-node-children", {
            "tree-node-children-closed": !isOpen
          })
        } >
          {children}
        </div>
        }
      </div>
    </div>
  );
});